import type {
  ConnectionState,
  DeviceRole,
  DeviceState,
  DisplayRole,
  Emotion,
  Environment,
  WalterMemory,
  WalterSurface,
  SystemAction,
  ToolType,
  WalterCommand,
  WalterSnapshot,
  WalterState,
  WalterTransport,
  WalterWireMessage,
} from "./types";
import { WALTER_PROTOCOL } from "./types";
import { initialWalterSnapshot, normalizeSnapshot, reduceWalter } from "./walterState";
import { isDurableWalterCommand, persistentWalterSnapshot } from "./persistence";

const CHANNEL = "walter-os-v4";
const STORAGE = "walter-os-snapshot-v4";
const wait = (duration: number) => new Promise((resolve) => window.setTimeout(resolve, duration));
const cloneSnapshot = (snapshot: WalterSnapshot): WalterSnapshot => structuredClone(snapshot);

function browserRole(): DisplayRole {
  const role = new URLSearchParams(window.location.search).get("display") ?? "main";
  return (["main", "third-eye", "heart", "simulator"] as const).includes(role as DisplayRole) ? role as DisplayRole : "main";
}

function restoreLocalSnapshot(): WalterSnapshot {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE) ?? "null") as WalterSnapshot | null;
    if (parsed && Date.now() - parsed.updatedAt < 86_400_000) {
      return normalizeSnapshot({ ...parsed, connection: { state: "standalone", reconnects: 0, lastMessageAt: parsed.updatedAt } });
    }
  } catch {
    // Corrupt prototype state should never prevent Walter from booting.
  }
  return normalizeSnapshot(initialWalterSnapshot);
}

export class BroadcastWalterTransport implements WalterTransport {
  readonly authoritative = false;
  private channel: BroadcastChannel | null;
  private listeners = new Set<(command: WalterCommand) => void>();

  constructor(channelName = CHANNEL) {
    this.channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(channelName);
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<{ kind: "command"; command: WalterCommand }>) => {
        if (event.data?.kind === "command") this.listeners.forEach((listener) => listener(event.data.command));
      };
    }
  }

  publish(command: WalterCommand) {
    this.channel?.postMessage({ kind: "command", command });
  }

  subscribe(listener: (command: WalterCommand) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  close() {
    this.channel?.close();
    this.listeners.clear();
  }
}

export class WebSocketWalterTransport implements WalterTransport {
  readonly authoritative = true;
  private socket: WebSocket | null = null;
  private closed = false;
  private reconnects = 0;
  private commandListeners = new Set<(command: WalterCommand) => void>();
  private snapshotListeners = new Set<(snapshot: WalterSnapshot) => void>();
  private connectionListeners = new Set<(state: ConnectionState) => void>();
  private queue: WalterCommand[] = [];
  private reconnectTimer: number | null = null;
  private readonly clientId = crypto.randomUUID?.() ?? `client-${Math.random().toString(36).slice(2)}`;

  constructor(private url: string, private role: DisplayRole = browserRole()) {
    this.connect();
  }

  private setConnection(state: ConnectionState) {
    this.connectionListeners.forEach((listener) => listener(state));
  }

  private connect() {
    if (this.closed) return;
    this.setConnection(this.reconnects ? "reconnecting" : "connecting");
    try {
      this.socket = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket.addEventListener("open", () => {
      this.setConnection("connected");
      this.socket?.send(JSON.stringify({ type: "hello", protocol: WALTER_PROTOCOL, role: this.role, clientId: this.clientId } satisfies WalterWireMessage));
      const pending = this.queue.splice(0);
      pending.forEach((command) => this.publish(command));
    });
    this.socket.addEventListener("message", (event) => {
      try {
        const message = JSON.parse(String(event.data)) as WalterWireMessage;
        if (message.protocol !== WALTER_PROTOCOL) return;
        if (message.type === "welcome" || message.type === "snapshot") {
          this.snapshotListeners.forEach((listener) => listener(normalizeSnapshot(message.snapshot)));
        } else if (message.type === "command") {
          this.commandListeners.forEach((listener) => listener(message.command));
        } else if (message.type === "ping") {
          this.socket?.send(JSON.stringify({ type: "pong", protocol: WALTER_PROTOCOL, now: message.now } satisfies WalterWireMessage));
        }
      } catch {
        // Ignore malformed wire messages; the daemon remains authoritative.
      }
    });
    this.socket.addEventListener("close", () => this.scheduleReconnect());
    this.socket.addEventListener("error", () => this.socket?.close());
  }

  private scheduleReconnect() {
    if (this.closed || this.reconnectTimer) return;
    this.socket = null;
    this.reconnects += 1;
    this.setConnection("reconnecting");
    const delay = Math.min(5000, 300 * 2 ** Math.min(this.reconnects, 4));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  publish(command: WalterCommand) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: "command", protocol: WALTER_PROTOCOL, command } satisfies WalterWireMessage));
      return;
    }
    if (this.queue.length < 32) this.queue.push(command);
  }

  subscribe(listener: (command: WalterCommand) => void) {
    this.commandListeners.add(listener);
    return () => this.commandListeners.delete(listener);
  }

  subscribeSnapshot(listener: (snapshot: WalterSnapshot) => void) {
    this.snapshotListeners.add(listener);
    return () => this.snapshotListeners.delete(listener);
  }

  subscribeConnection(listener: (state: ConnectionState) => void) {
    this.connectionListeners.add(listener);
    return () => this.connectionListeners.delete(listener);
  }

  close() {
    this.closed = true;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.commandListeners.clear();
    this.snapshotListeners.clear();
    this.connectionListeners.clear();
  }
}

function createBrowserTransport(): WalterTransport {
  const query = new URLSearchParams(window.location.search);
  const explicit = query.get("daemon");
  const shouldUseDaemon = explicit !== null || (location.hostname === "127.0.0.1" || location.hostname === "localhost") && location.port === "8787";
  if (shouldUseDaemon) {
    const url = explicit || `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
    return new WebSocketWalterTransport(url);
  }
  return new BroadcastWalterTransport();
}

export class WalterController {
  private snapshot: WalterSnapshot;
  private listeners = new Set<() => void>();
  private detachTransport: Array<() => void> = [];
  private turnToken = 0;
  private transferTimer: number | null = null;

  constructor(
    private transport: WalterTransport | null = null,
    seed: WalterSnapshot = initialWalterSnapshot,
  ) {
    this.snapshot = cloneSnapshot(normalizeSnapshot(seed));
    if (transport) {
      this.detachTransport.push(transport.subscribe((command) => this.dispatch(command, false)));
      if (transport.subscribeSnapshot) this.detachTransport.push(transport.subscribeSnapshot((snapshot) => this.replaceSnapshot(snapshot)));
      if (transport.subscribeConnection) this.detachTransport.push(transport.subscribeConnection((state) => this.setConnectionState(state)));
    }
  }

  getSnapshot = () => this.snapshot;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit() {
    this.listeners.forEach((listener) => listener());
  }

  private persist() {
    if (typeof document === "undefined" || typeof localStorage === "undefined") return;
    try { localStorage.setItem(STORAGE, JSON.stringify(persistentWalterSnapshot(this.snapshot))); } catch { /* storage is optional */ }
  }

  private replaceSnapshot(snapshot: WalterSnapshot) {
    const reconnects = this.snapshot.connection.reconnects;
    this.snapshot = normalizeSnapshot({
      ...snapshot,
      connection: { state: "connected", reconnects, lastMessageAt: Date.now() },
    });
    this.emit();
  }

  private setConnectionState(state: ConnectionState) {
    this.snapshot = {
      ...this.snapshot,
      connection: {
        state,
        reconnects: state === "reconnecting" ? this.snapshot.connection.reconnects + 1 : this.snapshot.connection.reconnects,
        lastMessageAt: state === "connected" ? Date.now() : this.snapshot.connection.lastMessageAt,
      },
    };
    this.emit();
  }

  dispatch(command: WalterCommand, publish = true) {
    const previousTransferId = this.snapshot.transfer?.id;
    this.snapshot = reduceWalter(this.snapshot, command);
    if (isDurableWalterCommand(command)) this.persist();
    this.emit();
    if (publish) this.transport?.publish(command);
    if (this.snapshot.transfer?.id !== previousTransferId) this.scheduleTransferClear(publish);
  }

  private scheduleTransferClear(publish: boolean) {
    if (typeof window === "undefined") return;
    if (this.transferTimer) window.clearTimeout(this.transferTimer);
    const transfer = this.snapshot.transfer;
    if (!transfer) { this.transferTimer = null; return; }
    const remaining = Math.max(0, transfer.durationMs - (Date.now() - transfer.startedAt));
    this.transferTimer = window.setTimeout(() => {
      this.transferTimer = null;
      if (this.snapshot.transfer?.id === transfer.id) this.dispatch({ type: "CLEAR_TRANSFER", id: transfer.id }, publish);
    }, remaining);
  }

  private toolForText(text: string): ToolType | null {
    if (/\b(remember|memory|save this|note that)\b/i.test(text)) return "memory";
    if (/\b(camera|look|see|vision|photo)\b/i.test(text)) return "vision";
    if (/\b(news|weather|internet|online|network)\b/i.test(text)) return "network";
    if (/\b(time|date|system|health|temperature|status|timer)\b/i.test(text)) return "system";
    return null;
  }

  private localResponse(text: string, tool: ToolType | null): string {
    if (tool === "memory") {
      const memory = text.replace(/^.*?\b(remember|save|note)\b[:\s-]*/i, "").trim() || text;
      this.dispatch({ type: "MEMORY_SAVE", text: memory });
      return `I stored this locally: ${memory}`;
    }
    if (tool === "vision") return "My vision adapter is responding, but no physical camera is attached to this browser. I will verify it during Pi bring-up.";
    if (tool === "network") return navigator.onLine
      ? "The network channel is available. A live source is not configured yet, so I will not manufacture a transmission."
      : "The network channel is offline. Local presence and memory remain available.";
    if (tool === "system") {
      if (/\btime\b/i.test(text)) return `It is ${new Intl.DateTimeFormat([], { hour: "numeric", minute: "2-digit" }).format(new Date())}.`;
      if (/\bdate\b/i.test(text)) return `Today is ${new Intl.DateTimeFormat([], { dateStyle: "full" }).format(new Date())}.`;
      return `Renderer online. Protocol ${WALTER_PROTOCOL}. Sequence ${this.snapshot.seq}. Brain ${this.snapshot.brain.status}. Connection ${this.snapshot.connection.state}.`;
    }
    return `I received your signal: “${text}” The local test brain is working; the agent adapter is the next intelligence layer.`;
  }

  private async runStandaloneTurn(text: string) {
    const token = ++this.turnToken;
    await wait(420);
    if (token !== this.turnToken) return;
    const tool = this.toolForText(text);
    if (tool) {
      this.dispatch({ type: "TOOL_START", tool });
      await wait(440);
      if (token !== this.turnToken) return;
      this.dispatch({ type: "TOOL_PROGRESS", progress: 0.62, label: `${tool} responding` });
      await wait(520);
      if (token !== this.turnToken) return;
      this.dispatch({ type: "TOOL_RETURN", tool });
      await wait(520);
    }
    if (token !== this.turnToken) return;
    const response = this.localResponse(text, tool);
    this.dispatch({ type: "COMPLETE_TURN", response, tool });
    await wait(Math.min(5200, Math.max(1900, response.length * 28)));
    if (token === this.turnToken) this.dispatch({ type: "FINISH_SPEAKING" });
  }

  setWalterState = (state: WalterState) => this.dispatch({ type: "SET_STATE", state });
  setEmotion = (emotion: Emotion) => this.dispatch({ type: "SET_EMOTION", emotion });
  setAudioLevel = (value: number) => this.dispatch({ type: "SET_AUDIO", value });
  setGaze = (x: number, y: number) => this.dispatch({ type: "SET_GAZE", gaze: { x, y } });
  setEnvironment = (environment: Environment) => this.dispatch({ type: "SET_ENVIRONMENT", environment });
  navigate = (surface: WalterSurface) => this.dispatch({ type: "NAVIGATE", surface });
  setOverlay = (overlay: WalterSnapshot["view"]["overlay"]) => this.dispatch({ type: "SET_OVERLAY", overlay });
  startListening = () => this.dispatch({ type: "START_LISTENING" });
  stopListening = () => this.dispatch({ type: "STOP_LISTENING" });
  setPrivacy = (privacy: "open" | "closed") => this.dispatch({ type: "SET_PRIVACY", privacy });
  trackVision = (x: number, y: number, confidence = 1) => this.dispatch({ type: "VISION_TRACK", target: { x, y }, confidence });
  captureVision = () => this.dispatch({ type: "VISION_CAPTURE" });
  saveMemory = (text: string, kind?: WalterMemory["kind"]) => this.dispatch({ type: "MEMORY_SAVE", text, kind });
  forgetMemory = (id: string) => this.dispatch({ type: "MEMORY_FORGET", id });
  queryMemory = (query: string) => this.dispatch({ type: "MEMORY_QUERY", query });
  createTimer = (label: string, durationMs: number) => {
    this.dispatch({ type: "TIMER_CREATE", label, durationMs });
    if (!this.transport?.authoritative) {
      const timer = this.snapshot.timers.items[0];
      if (timer) window.setTimeout(() => this.dispatch({ type: "TIMER_COMPLETE", id: timer.id }), Math.min(durationMs, 2_147_483_647));
    }
  };
  cancelTimer = (id: string) => this.dispatch({ type: "TIMER_CANCEL", id });
  refreshNews = () => {
    this.dispatch({ type: "NEWS_REFRESH" });
    if (!this.transport?.authoritative) window.setTimeout(() => this.dispatch({
      type: "NEWS_UPDATE",
      weather: navigator.onLine ? "Network present · live sources connect on Pi" : "Offline · last brief retained",
      signals: [
        { id: `signal-${Date.now()}`, source: "WALTER", title: "Embodiment channel is ready", detail: "The Pi bridge can replace browser reflexes without rebuilding this interface.", priority: "normal" },
        { id: `privacy-${Date.now()}`, source: "PRIVACY", title: this.snapshot.vision.privacy === "closed" ? "Vision shutter is closed" : "Vision is locally active", detail: "No frame leaves the device without an explicit tool request.", priority: "quiet" },
      ],
    }), 800);
  };
  setSetting = <K extends keyof WalterSnapshot["settings"]>(key: K, value: WalterSnapshot["settings"][K]) => this.dispatch({ type: "SET_SETTING", key, value });
  resolveApproval = (approved: boolean) => this.dispatch({ type: "RESOLVE_APPROVAL", approved });
  requestSystemAction = (action: SystemAction, title: string, detail: string) => this.dispatch({ type: "REQUEST_APPROVAL", action, title, detail });
  triggerToolCall = (tool: ToolType) => this.dispatch({ type: "TOOL_START", tool });
  triggerToolReturn = (tool: ToolType) => this.dispatch({ type: "TOOL_RETURN", tool });
  beginTurn = () => this.dispatch({ type: "BEGIN_TURN" });
  submitText = (text: string) => {
    if (!text.trim()) return;
    this.dispatch({ type: "SUBMIT_TEXT", text });
    if (!this.transport?.authoritative) void this.runStandaloneTurn(text.trim());
  };
  cancelTurn = () => {
    this.turnToken += 1;
    this.dispatch({ type: "CANCEL_TURN" });
  };
  setDevice = (device: DeviceRole, status: DeviceState) => this.dispatch({ type: "SET_DEVICE", device, status });
  wake = () => this.dispatch({ type: "WAKE" });
  sleep = () => {
    this.turnToken += 1;
    this.dispatch({ type: "SLEEP" });
  };

  destroy() {
    if (this.transferTimer && typeof window !== "undefined") window.clearTimeout(this.transferTimer);
    this.detachTransport.forEach((detach) => detach());
    this.transport?.close();
    this.listeners.clear();
  }
}

const browserRuntime = typeof document !== "undefined" && typeof window !== "undefined";
const restored = browserRuntime ? restoreLocalSnapshot() : initialWalterSnapshot;
export const walterController = new WalterController(browserRuntime ? createBrowserTransport() : null, restored);

export interface WalterPublicController {
  setWalterState: (state: WalterState) => void;
  setEmotion: (emotion: Emotion) => void;
  setAudioLevel: (value: number) => void;
  setGaze: (x: number, y: number) => void;
  setEnvironment: (environment: Environment) => void;
  navigate: (surface: WalterSurface) => void;
  setOverlay: (overlay: WalterSnapshot["view"]["overlay"]) => void;
  startListening: () => void;
  stopListening: () => void;
  setPrivacy: (privacy: "open" | "closed") => void;
  trackVision: (x: number, y: number, confidence?: number) => void;
  captureVision: () => void;
  saveMemory: (text: string, kind?: WalterMemory["kind"]) => void;
  forgetMemory: (id: string) => void;
  queryMemory: (query: string) => void;
  createTimer: (label: string, durationMs: number) => void;
  cancelTimer: (id: string) => void;
  refreshNews: () => void;
  setSetting: <K extends keyof WalterSnapshot["settings"]>(key: K, value: WalterSnapshot["settings"][K]) => void;
  resolveApproval: (approved: boolean) => void;
  requestSystemAction: (action: SystemAction, title: string, detail: string) => void;
  triggerToolCall: (tool: ToolType) => void;
  triggerToolReturn: (tool: ToolType) => void;
  beginTurn: () => void;
  submitText: (text: string) => void;
  cancelTurn: () => void;
  setDevice: (device: DeviceRole, status: DeviceState) => void;
  wake: () => void;
  sleep: () => void;
  getSnapshot: () => WalterSnapshot;
}

export const publicWalterController: WalterPublicController = {
  setWalterState: walterController.setWalterState,
  setEmotion: walterController.setEmotion,
  setAudioLevel: walterController.setAudioLevel,
  setGaze: walterController.setGaze,
  setEnvironment: walterController.setEnvironment,
  navigate: walterController.navigate,
  setOverlay: walterController.setOverlay,
  startListening: walterController.startListening,
  stopListening: walterController.stopListening,
  setPrivacy: walterController.setPrivacy,
  trackVision: walterController.trackVision,
  captureVision: walterController.captureVision,
  saveMemory: walterController.saveMemory,
  forgetMemory: walterController.forgetMemory,
  queryMemory: walterController.queryMemory,
  createTimer: walterController.createTimer,
  cancelTimer: walterController.cancelTimer,
  refreshNews: walterController.refreshNews,
  setSetting: walterController.setSetting,
  resolveApproval: walterController.resolveApproval,
  requestSystemAction: walterController.requestSystemAction,
  triggerToolCall: walterController.triggerToolCall,
  triggerToolReturn: walterController.triggerToolReturn,
  beginTurn: walterController.beginTurn,
  submitText: walterController.submitText,
  cancelTurn: walterController.cancelTurn,
  setDevice: walterController.setDevice,
  wake: walterController.wake,
  sleep: walterController.sleep,
  getSnapshot: walterController.getSnapshot,
};

export const setWalterState = publicWalterController.setWalterState;
export const setEmotion = publicWalterController.setEmotion;
export const setAudioLevel = publicWalterController.setAudioLevel;
export const setGaze = publicWalterController.setGaze;
export const triggerToolCall = publicWalterController.triggerToolCall;
export const triggerToolReturn = publicWalterController.triggerToolReturn;

export { initialWalterSnapshot, reduceWalter } from "./walterState";

declare global {
  interface Window {
    walter: WalterPublicController;
  }
}
